import { useCallback } from 'react';
import { ConfirmMsg } from '../InfrastructureHelper';
import { useVirtualInfrastructureInit } from './UseVirtualInfrastructureInit';

/**
 * Custom hook to manage dialog-related logic for virtual infrastructure list.
 * Handles confirmation message generation and determination of whether confirmation is needed.
 *
 * This hook encapsulates the dialog management logic for:
 * - Generating confirmation messages for actions (enable/disable)
 * - Determining if user confirmation is required based on selected types
 *
 * The hook uses functions from useVirtualInfrastructureInit to extract sub-types
 * and check confirmation requirements.
 *
 * @returns {Object} Dialog utilities
 * @returns {Function} getConfirmMsg - Generate confirmation message for action
 * @returns {Function} isNeedConfirm - Determine if confirmation is needed
 *
 * @example
 * function VirtualInfrastructureList() {
 *   const { getConfirmMsg, isNeedConfirm } = useVirtualInfrastructureDialogs();
 *
 *   return (
 *     <ConfirmDataTableActionButton
 *       confirmMsg={() => getConfirmMsg(action, selectionData.current)}
 *       needConfirm={isNeedConfirm(selectionData.current)}
 *     />
 *   );
 * }
 */
export function useVirtualInfrastructureDialogs() {
    const { extractSelectedSubTypes, hasTypeRequiringConfirmation } = useVirtualInfrastructureInit();

    /**
     * Generate confirmation message for an action based on selected types.
     * Returns a React component with the confirmation message, or undefined if no selection.
     *
     * @param {string} action - The action being performed (e.g., 'enable', 'disable')
     * @param {Object} selectionData - Current table selection data
     * @returns {JSX.Element|undefined} Confirmation message component or undefined
     */
    const getConfirmMsg = useCallback(
        (action, selectionData) => {
            const selectSubTypeSet = extractSelectedSubTypes(selectionData);
            if (!selectSubTypeSet) {
                return undefined;
            }
            return <ConfirmMsg action={action} selectSubTypeSet={selectSubTypeSet} />;
        },
        [extractSelectedSubTypes]
    );

    /**
     * Determine if confirmation dialog should be shown based on selected types.
     * vCenter and Hypervisor types require confirmation for certain actions.
     *
     * @param {Object} selectionData - Current table selection data
     * @returns {boolean} True if confirmation dialog should be shown
     */
    const isNeedConfirm = useCallback(
        (selectionData) => {
            const selectSubTypeSet = extractSelectedSubTypes(selectionData);
            return hasTypeRequiringConfirmation(selectSubTypeSet);
        },
        [extractSelectedSubTypes, hasTypeRequiringConfirmation]
    );

    return {
        getConfirmMsg,
        isNeedConfirm,
    };
}
